import React, { useContext, useEffect } from 'react';
import { Drawer, useMediaQuery } from '@mui/material';
import { drawerClasses } from '@mui/material/Drawer';
import { useTheme } from '@mui/material/styles';
import SidebarPanel from 'components/shared/sidebar/SidebarPanel';
import SidebarDrawerContext from 'context/SidebarDrawerContext';

/**
 * Drawer de navegación MOBILE (<lg). Lo abre la foto de la barra inferior; desliza desde la
 * izquierda el MISMO panel del desktop (buscador, menú del rol, toggle de tema), con scrim.
 */
function MobileSidebarDrawer() {
  const { closeMobileSidebar, mobileOpen } = useContext(SidebarDrawerContext);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  // Al pasar a desktop el Drawer se DESMONTA, pero `mobileOpen` seguía en true: al rotar el
  // teléfono a horizontal y volver, reaparecía abierto solo, con su velo puesto.
  useEffect(() => {
    if (isDesktop) closeMobileSidebar();
  }, [isDesktop, closeMobileSidebar]);

  if (isDesktop) return null;

  return (
    <Drawer
      anchor="left"
      open={mobileOpen}
      onClose={closeMobileSidebar}
      // SALIDA INSTANTÁNEA (exit: 0). Esto es lo que arregla el panel que se quedaba abierto.
      //
      // MUI mantiene el Modal pintado hasta que su animación de salida avisa de que terminó, y ese
      // aviso llega por `transitionend`. Al tocar un ítem del menú se navega a la vez, y como las
      // pantallas cargan en diferido, React SUSPENDE el árbol y lo oculta con `display:none`
      // mientras trae el código. Un elemento oculto no emite `transitionend`, así que el aviso no
      // llegaba nunca y el panel se quedaba visible aunque su estado ya fuera "cerrado" y MUI ya le
      // hubiera puesto `aria-hidden`. Sin transición de salida no hay nada que esperar. La de
      // ENTRADA se conserva, que es la que se aprecia al abrirlo.
      transitionDuration={{ enter: 225, exit: 0 }}
      // SIN `keepMounted`, y es deliberado: con él, el Modal solo se oculta cuando su transición
      // avisa, justo la dependencia que acabamos de quitar. Además el tour ya no necesita las
      // anclas en el DOM con el panel cerrado, porque filtra por visibilidad real.
      //
      // disableEnforceFocus: el tooltip de react-joyride vive fuera del modal; con el focus-trap
      // activo sus botones pelean el foco y el tour se traba.
      ModalProps={{ disableEnforceFocus: true }}
      slotProps={{ backdrop: { sx: { backgroundColor: theme.palette.scrim } } }}
      sx={{
        [`& .${drawerClasses.paper}`]: {
          backgroundColor: 'surface.sidebar',
          backgroundImage: 'none',
          border: `1px solid ${theme.palette.hairline.main}`,
          borderRadius: '16px',
          boxShadow: theme.palette.shadow.card,
          boxSizing: 'border-box',
          height: 'calc(100dvh - 24px)',
          margin: '12px',
          overflow: 'hidden',
          width: 'min(300px, calc(100vw - 56px))'
        }
      }}>
      <SidebarPanel variant="mobile" />
    </Drawer>
  );
}

export default MobileSidebarDrawer;
